import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

// Type definitions
export type DealStatusType = 'draft' | 'active' | 'expiring' | 'expired' | 'archived'
export type ExpiryPolicyType = 'extend' | 'expire'
export type UpdateRequestType = 'activate' | 'extend' | 'expire' | 'archive'
export type TermStatusType = 'not_taken' | 'taken' | 'active' | 'upcoming' | 'expired'

// Union type for all possible status types
export type StatusType =
  | { type: 'deal'; status: DealStatusType }
  | { type: 'expiryPolicy'; status: ExpiryPolicyType }
  | { type: 'updateRequest'; status: UpdateRequestType }
  | { type: 'term'; status: TermStatusType }

interface StatusBadgeProps {
  status: StatusType
  className?: string
}

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const getStatusStyles = (): string => {
    const { type, status: value } = status

    // Deal status styling
    if (type === 'deal') {
      switch (value) {
        case 'draft': return "border-slate-200 bg-slate-50 text-slate-600"
        case 'active': return "border-emerald-500 bg-emerald-500 text-white"
        case 'expiring': return "border-amber-500 bg-amber-500 text-white"
        case 'expired': return "border-rose-500 bg-rose-500 text-white"
        case 'archived': return "border-zinc-400 bg-zinc-50 text-zinc-600"
        default: return ""
      }
    }

    // Expiry policy styling
    if (type === 'expiryPolicy') {
      switch (value) {
        case 'extend': return "border-blue-500 bg-blue-500 text-white"
        case 'expire': return "border-rose-500 bg-rose-50 text-rose-700"
        default: return ""
      }
    }

    // Update request styling
    if (type === 'updateRequest') {
      switch (value) {
        case 'activate': return "border-emerald-500 bg-emerald-500 text-white"
        case 'extend': return "border-blue-500 bg-blue-500 text-white"
        case 'expire': return "border-amber-500 bg-amber-50 text-amber-700"
        case 'archive': return "border-zinc-400 bg-zinc-50 text-zinc-600"
        default: return ""
      }
    }

    // Term status styling
    if (type === 'term') {
      switch (value) {
        case 'not_taken': return "border-slate-200 bg-slate-50 text-slate-700"
        case 'taken': return "border-violet-500 bg-violet-500 text-white"
        case 'active': return "border-emerald-500 bg-emerald-500 text-white"
        case 'upcoming': return "border-cyan-500 bg-cyan-500 text-white"
        case 'expired': return "border-zinc-400 text-zinc-700"
        default: return ""
      }
    }

    return ""
  }

  const getVariant = (): "default" | "outline" => {
    const { type, status: value } = status

    // For deal status
    if (type === 'deal') {
      return ['draft', 'expired', 'archived'].includes(value as DealStatusType) ? 'outline' : 'default'
    }

    // For expiry policy
    if (type === 'expiryPolicy') {
      return value === 'expire' ? 'outline' : 'default'
    }

    // For update request
    if (type === 'updateRequest') {
      return ['expire', 'archived'].includes(value as UpdateRequestType) ? 'outline' : 'default'
    }

    // For term status
    if (type === 'term') {
      return ['not_taken', 'expired'].includes(value as TermStatusType) ? 'outline' : 'default'
    }

    return 'default'
  }

  const formatLabel = (input: string): string => {
    // Handle specific cases
    if (input === 'not_taken') return 'Not Taken'

    // Default formatting: capitalize each word
    return input
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <Badge
      variant={getVariant()}
      className={cn(
        "border-2 font-medium rounded-md",
        getStatusStyles(),
        className
      )}
    >
      {formatLabel(status.status)}
    </Badge>
  )
}

// Example usage component
export function StatusBadgeExamples() {
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h2 className="text-lg font-medium">Deal Status Badges</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={{ type: 'deal', status: 'draft' }} />
          <StatusBadge status={{ type: 'deal', status: 'active' }} />
          <StatusBadge status={{ type: 'deal', status: 'expiring' }} />
          <StatusBadge status={{ type: 'deal', status: 'expired' }} />
          <StatusBadge status={{ type: 'deal', status: 'archived' }} />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-medium">Expiry Policy Badges</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={{ type: 'expiryPolicy', status: 'extend' }} />
          <StatusBadge status={{ type: 'expiryPolicy', status: 'expire' }} />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-medium">Update Request Badges</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={{ type: 'updateRequest', status: 'activate' }} />
          <StatusBadge status={{ type: 'updateRequest', status: 'extend' }} />
          <StatusBadge status={{ type: 'updateRequest', status: 'expire' }} />
          <StatusBadge status={{ type: 'updateRequest', status: 'archived' }} />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-medium">Term Status Badges</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge status={{ type: 'term', status: 'not_taken' }} />
          <StatusBadge status={{ type: 'term', status: 'taken' }} />
          <StatusBadge status={{ type: 'term', status: 'active' }} />
          <StatusBadge status={{ type: 'term', status: 'upcoming' }} />
          <StatusBadge status={{ type: 'term', status: 'expired' }} />
        </div>
      </div>
    </div>
  )
}
